import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  checkWasChanged,
  getGetterByKey,
  noNeedToCheckPrevKeys,
  rideHistoryHeaders,
  type RideHistoryHeaderKeys,
} from "@ryde/layout";
import { useSelectItemsTranslate } from "@ryde/redux";
import { useFormats, type IRideInvestigationHistoryDto } from "@ryde/shared";

import { buildAuditChanges } from "./investigation.audit-changes.utils";
import { INVESTIGATION_HISTORY } from "./investigation.constants";
import type {
  IInvestigationAuditChanges,
  IInvestigationAuditColumn,
} from "./investigation.models";

/** What each audited change in the history did, in the ride history drawer's columns. Keyed by audit row id. */
export const useInvestigationAuditChanges = (
  history?: IRideInvestigationHistoryDto,
): Map<number, IInvestigationAuditChanges> => {
  const { t } = useTranslation();
  const { translateLabel } = useSelectItemsTranslate();
  const { formatDateTimeNormalized, formatDateTimeWithSecondsNormalized } =
    useFormats();

  return useMemo(() => {
    if (!history) {
      return new Map();
    }

    const columns = (Object.keys(rideHistoryHeaders) as RideHistoryHeaderKeys[])
      .filter(
        key =>
          !noNeedToCheckPrevKeys.includes(key) &&
          !INVESTIGATION_HISTORY.auditSkippedColumns.has(key),
      )
      .map(key => {
        const column: IInvestigationAuditColumn = {
          key,
          labelKey: rideHistoryHeaders[key],
          read: item =>
            getGetterByKey(key)({
              item,
              formatDateTimeNormalized,
              formatDateTimeWithSecondsNormalized,
              translationFn: t,
              translateLabel,
            }),
        };

        return column;
      });

    return buildAuditChanges({
      entries: history.entries,
      rideId: history.rideId,
      columns,
      isChanged: checkWasChanged,
      formatDateTimeNormalized,
    });
  }, [
    history,
    t,
    translateLabel,
    formatDateTimeNormalized,
    formatDateTimeWithSecondsNormalized,
  ]);
};
